import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, StatusBadge, Table } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { PageSkeleton } from '../../components/PageSkeleton';

function mapStatus(status: string): 'fresh' | 'stale' | 'partial' {
  if (status === 'complete' || status === 'success' || status === 'healthy') return 'fresh';
  if (status === 'partial' || status === 'attention') return 'partial';
  return 'stale';
}

export function OperationsPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['operations'], queryFn: api.operations });

  if (isLoading) return <PageSkeleton blocks={3} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Operations health unavailable" />;

  const sources = data.sources ?? [];
  const jobs = data.jobs ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="Operations" subtitle="Source health, freshness, and audit." />
      <ExplainActions screenName="Operations" />

      {(data.staleMetrics?.length ?? 0) > 0 && (
        <DataQualityState
          state="stale"
          title="Stale metrics detected"
          description={data.staleMetrics?.join(', ')}
        />
      )}

      <AnalyticalPanel title="Source freshness">
        <Table
          caption="Source health"
          rows={sources}
          getRowKey={(r) => r.sourceId}
          columns={[
            { key: 'id', header: 'Source', cell: (r) => r.sourceId },
            {
              key: 'status',
              header: 'Status',
              cell: (r) => <StatusBadge status={mapStatus(r.status)}>{r.status}</StatusBadge>,
            },
            { key: 'time', header: 'Last success', cell: (r) => r.lastSuccess?.slice(0, 16) ?? '—' },
            { key: 'cadence', header: 'Cadence', cell: (r) => r.cadence },
          ]}
        />
      </AnalyticalPanel>

      <AnalyticalPanel title="Scheduled jobs">
        <Table
          caption="Job runs"
          rows={jobs}
          getRowKey={(r) => r.job}
          columns={[
            { key: 'job', header: 'Job', cell: (r) => r.job },
            {
              key: 'status',
              header: 'Status',
              cell: (r) => <StatusBadge status={mapStatus(r.status)}>{r.status}</StatusBadge>,
            },
            { key: 'run', header: 'Last run', cell: (r) => r.lastRun?.slice(0, 16) ?? '—' },
          ]}
        />
      </AnalyticalPanel>
    </div>
  );
}
